import { PerspectiveCamera } from '@react-three/drei';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Object3D } from 'three';

import type { CharacterMeshFormat } from '@cartwhl/3d/player';
import { Player } from '@cartwhl/3d/player';
import type { MotionConfigSource } from '@cartwhl/common/api/getMotion';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import Button from '@cartwhl/ui/input/Button';
import FrostedGlass from '@cartwhl/ui/utilities/FrostedGlass';

import { RetargetRefreshIcon } from '../../../presentations/icons';
import { CARTOGRAPHER_VIEWPORT_LOADING_OVERLAY_BACKGROUND } from '../../Viewer/constants';
import {
  MOTION_MINI_FROSTED_FRAME_CLASS,
  MOTION_MINI_VIEWPORT_PLACEHOLDER_CLASS,
} from '../constants';
import CartographerMotionPreviewAssetPlayer from './CartographerMotionPreviewAssetPlayer';
import CartographerPreviewCharacter from '../CartographerPreviewCharacter';
import { DEFAULT_MOTION_EXPORT_SETTINGS } from '../motionExport';
import {
  resolveMotionPreviewAssetKind,
  resolveMotionPreviewDownloadType,
  type CartographerMotionPreviewAssetKind,
  type CartographerMotionPreviewGltfAsset,
} from '../motionPreviewAsset';
import {
  getMotionExportSettingsKey,
  type CartographerMotionRetargetStatus,
} from '../motionRetargetStatus';
import useCartographerMotionRetargetPreview from '../useCartographerMotionRetargetPreview';
import useMotionPreviewGltfAsset from '../useMotionPreviewGltfAsset';

interface CartographerMotionMiniProps {
  active: boolean;
  apiKey: string;
  characterID: string;
  meshFile: File | null;
  motionID: string | null;
  motionLabel?: string | null;
  /** Zero-based body index for multi-person comic jobs. */
  bodyIndex?: number;
  fileVersion: number;
  mappingVersion: number;
  bonePropertiesVersion: number;
  configSource?: MotionConfigSource;
  prepareForUserMotionRequest?: (options: {
    configSource: MotionConfigSource;
    mode: 'auto' | 'manual';
  }) => Promise<boolean> | boolean;
  onSwitchToUnsavedRetarget?: () => Promise<boolean> | boolean;
  onRetargetStatusChange?: (status: CartographerMotionRetargetStatus) => void;
  onOpenFullPreview: () => void;
  onClose: () => void;
}

const MINI_ACTOR_USER = 'cartographer-mini-user';

interface CartographerMotionMiniPlayerProps {
  assetKind: CartographerMotionPreviewAssetKind;
  characterID: string;
  meshFile: File | null;
  meshFormat?: CharacterMeshFormat;
  meshPath: string;
  refreshKey: string;
  motionAsset: CartographerMotionPreviewGltfAsset | null;
  motionUrl: string;
  userCharacterScene: Object3D | null;
  onSceneReady: (scene: Object3D | null) => void;
}

function CartographerMotionMiniPlayer({
  assetKind,
  characterID,
  meshFile,
  meshFormat,
  meshPath,
  refreshKey,
  motionAsset,
  motionUrl,
  userCharacterScene,
  onSceneReady,
}: CartographerMotionMiniPlayerProps) {
  return (
    <Player.Root
      key={`${assetKind}|${motionUrl}|${refreshKey}`}
      id="cartographer-motion-mini-preview"
      initialSettings={{
        playback: { autoplay: true, loop: true },
        character: { showSkeleton: false },
      }}
    >
      <Player.Canvas className="h-full w-full rounded-lg">
        <Player.Environment />
        <PerspectiveCamera makeDefault position={[0, 1, 10]} zoom={2} />
        <Player.CameraControls
          fitCharacter
          fitCharacterKey={`${assetKind}|${motionUrl}|${refreshKey}`}
          fitCharacterTarget={userCharacterScene}
        />
        {assetKind === 'gltf' && motionAsset ? (
          <CartographerMotionPreviewAssetPlayer
            actorId={MINI_ACTOR_USER}
            motionAsset={motionAsset}
            onSceneReady={onSceneReady}
          />
        ) : (
          <CartographerPreviewCharacter
            actorId={MINI_ACTOR_USER}
            characterID={characterID}
            meshFile={meshFile}
            meshPath={meshPath}
            meshFormat={meshFormat}
            bvhUrl={motionUrl}
            onSceneReady={onSceneReady}
          />
        )}
      </Player.Canvas>
    </Player.Root>
  );
}

function inferMeshFormatFromFile(file: File | null): CharacterMeshFormat | undefined {
  if (!file) return undefined;
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.fbx')) return 'fbx';
  if (lower.endsWith('.glb')) return 'glb';
  if (lower.endsWith('.gltf')) return 'gltf';
  return undefined;
}

export default function CartographerMotionMini({
  active,
  apiKey,
  characterID,
  meshFile,
  motionID,
  motionLabel = null,
  bodyIndex,
  fileVersion,
  mappingVersion,
  bonePropertiesVersion,
  configSource = 'saved',
  prepareForUserMotionRequest,
  onSwitchToUnsavedRetarget,
  onRetargetStatusChange,
  onOpenFullPreview,
  onClose,
}: CartographerMotionMiniProps) {
  const [meshPath, setMeshPath] = useState<string | null>(null);
  const [userCharacterScene, setUserCharacterScene] = useState<Object3D | null>(
    null,
  );

  const meshFormat = useMemo(() => inferMeshFormatFromFile(meshFile), [meshFile]);
  const motionPreviewAssetKind = useMemo(
    () =>
      resolveMotionPreviewAssetKind({
        meshFormat,
        fileName: meshFile?.name ?? null,
      }),
    [meshFile, meshFormat],
  );
  const motionPreviewDownloadType = useMemo(
    () => resolveMotionPreviewDownloadType(motionPreviewAssetKind),
    [motionPreviewAssetKind],
  );

  useEffect(() => {
    if (!active || !meshFile) {
      setMeshPath(null);
      setUserCharacterScene(null);
      return;
    }
    const nextPath = URL.createObjectURL(meshFile);
    setMeshPath(nextPath);
    return () => {
      URL.revokeObjectURL(nextPath);
    };
  }, [active, meshFile]);

  const refreshKey = useMemo(
    () => `${characterID}|${motionID ?? ''}|${bodyIndex ?? 0}|${fileVersion}`,
    [bodyIndex, characterID, fileVersion, motionID],
  );
  const mappingRevisionKey = useMemo(
    () => `${mappingVersion}|${bonePropertiesVersion}`,
    [bonePropertiesVersion, mappingVersion],
  );
  const hasPreviewPrerequisites = Boolean(apiKey && characterID);

  const { motionUrl, loading, mainError, previewOutOfDate, refreshNow } =
    useCartographerMotionRetargetPreview({
      enabled:
        active && Boolean(meshPath) && Boolean(motionID) && hasPreviewPrerequisites,
      apiKey,
      motionID,
      characterID,
      bodyIndex,
      configSource,
      downloadType: motionPreviewDownloadType,
      fps: 30,
      frameStep: 1,
      rootMotion: true,
      refreshKey,
      stateRevisionKey: mappingRevisionKey,
      resetOnDisable: false,
      prepareForUserMotionRequest,
    });
  const {
    asset: userMotionAsset,
    error: userMotionAssetError,
    isLoading: userMotionAssetLoading,
  } = useMotionPreviewGltfAsset({
    enabled:
      active &&
      motionPreviewAssetKind === 'gltf' &&
      Boolean(motionUrl) &&
      Boolean(motionID) &&
      hasPreviewPrerequisites,
    gltfUrl: motionPreviewAssetKind === 'gltf' ? motionUrl : null,
  });
  const previewLoading = loading || userMotionAssetLoading;
  const previewError = mainError ?? userMotionAssetError?.message ?? null;

  useEffect(() => {
    onRetargetStatusChange?.({
      source: 'mini',
      motionID,
      bodyIndex,
      configSource,
      mappingRevisionKey,
      exportSettingsKey: getMotionExportSettingsKey(
        DEFAULT_MOTION_EXPORT_SETTINGS,
      ),
      userMotionUrl: motionUrl,
      loading,
      mainError,
      previewOutOfDate,
    });
  }, [
    bodyIndex,
    configSource,
    loading,
    mainError,
    mappingRevisionKey,
    motionID,
    onRetargetStatusChange,
    previewOutOfDate,
    motionUrl,
  ]);

  const cardTitle = motionLabel?.trim() || motionID || 'No motion selected';
  const hasMotionSelection = Boolean(motionID && meshFile);
  const canRenderPreview = active && hasMotionSelection && hasPreviewPrerequisites;
  const requirementsMessage = !apiKey
    ? !characterID
      ? 'Add an API key and save this character to enable retarget preview.'
      : 'Add an API key to enable retarget preview.'
    : !characterID
      ? 'Save this character to enable retarget preview.'
      : null;
  const showRetargetRefreshBadge =
    active &&
    canRenderPreview &&
    Boolean(motionUrl) &&
    !mainError &&
    previewOutOfDate;

  const canOpenExpandedPreview = active && canRenderPreview;

  const handleRefreshPreview = useCallback(async () => {
    if (configSource === 'temp') {
      refreshNow();
      return;
    }
    if (!onSwitchToUnsavedRetarget) return;
    const didSwitch = await onSwitchToUnsavedRetarget();
    if (!didSwitch) return;
    refreshNow({ configSource: 'temp' });
  }, [configSource, onSwitchToUnsavedRetarget, refreshNow]);

  const motionMiniPlayer =
    active &&
    motionUrl &&
    (motionPreviewAssetKind === 'gltf'
      ? Boolean(userMotionAsset)
      : Boolean(meshPath)) ? (
      <CartographerMotionMiniPlayer
        assetKind={motionPreviewAssetKind}
        characterID={characterID}
        meshFile={meshFile}
        meshPath={meshPath ?? ''}
        meshFormat={meshFormat}
        refreshKey={refreshKey}
        motionAsset={userMotionAsset}
        motionUrl={motionUrl}
        userCharacterScene={userCharacterScene}
        onSceneReady={setUserCharacterScene}
      />
    ) : null;

  return (
    <div className="pointer-events-none relative z-[35] w-full">
      <div
        className={active ? 'pointer-events-auto' : 'pointer-events-none'}
        // When Stage (or preview off) is selected, the dock still mounts this panel
        // invisibly; without inert, descendants with pointer-events:auto still capture
        // clicks (e.g. opening full modal from the hidden preview hit region).
        inert={!active}
      >
        <FrostedGlass className={MOTION_MINI_FROSTED_FRAME_CLASS}>
          <div className="flex min-h-0 min-w-0 flex-col">
            <div className="border-gray-30/70 dark:border-gray-60/70 flex items-center justify-between gap-2 border-b px-2 py-1.5">
              <div className="min-w-0 flex-1">
                <Tooltip text="Mini preview" side="top" inModal>
                  <div className="min-w-0">
                    <Text
                      as="h3"
                      variant="studio/body/medium/sm"
                      className={`truncate text-primary ${previewOutOfDate ? 'italic' : ''}`}
                      title={cardTitle}
                    >
                      {cardTitle}
                    </Text>
                  </div>
                </Tooltip>
              </div>
              <div className="flex items-center gap-0.5">
                <Tooltip text="Close" side="top" inModal>
                  <Button
                    data-mini-control="true"
                    variant="tertiary"
                    size="sm"
                    className="h-7 w-7"
                    icon={<CrossIcon className="h-4 w-4" />}
                    aria-label="Close mini preview"
                    onClick={onClose}
                  />
                </Tooltip>
              </div>
            </div>

            <div
              className={`relative h-[150px] w-full px-1.5 pb-1.5 pt-1.5 ${
                canOpenExpandedPreview ? 'cursor-pointer' : ''
              }`}
              role={canOpenExpandedPreview ? 'button' : undefined}
              tabIndex={canOpenExpandedPreview ? 0 : undefined}
              aria-label={
                canOpenExpandedPreview ? 'Open full motion preview' : undefined
              }
              onClick={canOpenExpandedPreview ? onOpenFullPreview : undefined}
              onKeyDown={
                canOpenExpandedPreview
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onOpenFullPreview();
                      }
                    }
                  : undefined
              }
            >
              {showRetargetRefreshBadge && (
                <div className="absolute top-3 right-3 z-20">
                  <Tooltip
                    text={
                      loading
                        ? 'Retargeting using current mapping…'
                        : configSource === 'temp'
                          ? 'Unsaved mapping changed. Retarget and refresh preview.'
                          : 'Mapping changed. Retarget using current in-editor mapping.'
                    }
                    side="top"
                    inModal
                  >
                    <Button
                      data-mini-control="true"
                      variant="tertiary"
                      size="sm"
                      className="h-8 w-8"
                      loading={loading}
                      disabled={loading}
                      icon={<RetargetRefreshIcon className="h-5 w-5" />}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleRefreshPreview();
                      }}
                      aria-label="Retarget and refresh preview"
                    />
                  </Tooltip>
                </div>
              )}
              {!active ? null : !hasMotionSelection ? (
                <div
                  className={`border-gray-30/50 dark:text-gray-40 flex h-full items-center justify-center rounded-lg border border-dashed px-3 text-center text-gray-50 ${MOTION_MINI_VIEWPORT_PLACEHOLDER_CLASS}`}
                >
                  <Text variant="studio/body/medium/sm">
                    Choose a motion to start previewing retargeted movement.
                  </Text>
                </div>
              ) : !canRenderPreview ? (
                <div
                  className={`border-gray-30/50 dark:text-gray-40 flex h-full items-center justify-center rounded-lg border border-dashed px-3 text-center text-gray-50 ${MOTION_MINI_VIEWPORT_PLACEHOLDER_CLASS}`}
                >
                  <Text variant="studio/body/medium/sm">
                    {requirementsMessage}
                  </Text>
                </div>
              ) : previewError ? (
                <div
                  className={`flex h-full items-center justify-center rounded-lg px-3 text-center ${MOTION_MINI_VIEWPORT_PLACEHOLDER_CLASS}`}
                >
                  <Text variant="studio/body/medium/sm" className="text-red-60">
                    {previewError}
                  </Text>
                </div>
              ) : !meshPath ||
                !motionUrl ||
                (motionPreviewAssetKind === 'gltf' && !userMotionAsset) ? (
                <div
                  className={`relative flex h-full min-h-0 w-full flex-col items-center justify-center overflow-hidden rounded-lg text-center ${MOTION_MINI_VIEWPORT_PLACEHOLDER_CLASS}`}
                  role="status"
                  aria-live="polite"
                  aria-busy={previewLoading}
                >
                  <div
                    className="pointer-events-none absolute inset-0 animate-pulse rounded-lg"
                    style={{
                      background: CARTOGRAPHER_VIEWPORT_LOADING_OVERLAY_BACKGROUND,
                    }}
                    aria-hidden
                  />
                  <div className="relative z-10 flex max-w-[220px] flex-col items-center gap-2 px-3">
                    <Loading
                      className="h-5 w-5 text-secondary"
                      trackClassName="stroke-current opacity-20"
                      strokeWidth={3}
                    />
                    <Text
                      as="p"
                      variant="studio/body/medium/sm"
                      className="text-secondary"
                    >
                      {motionPreviewAssetKind === 'gltf' && motionUrl
                        ? 'Loading motion preview…'
                        : 'Retargeting motion…'}
                    </Text>
                  </div>
                </div>
              ) : configSource === 'temp' ? (
                <Tooltip
                  text="Using unsaved in-editor mapping for this retargeting."
                  side="top"
                  inModal
                >
                  <div className="h-full w-full">{motionMiniPlayer}</div>
                </Tooltip>
              ) : (
                motionMiniPlayer
              )}
            </div>
          </div>
        </FrostedGlass>
      </div>
    </div>
  );
}
